import React, { useState, useEffect } from 'react';
import { useAuth, usePlayer } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { 
  Users, Video, Eye, Heart, Clock, DollarSign, TrendingUp, 
  Gift, BarChart3, Crown, Loader2, Trophy, Sparkles, Play
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

export const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [trendingCreators, setTrendingCreators] = useState([]);
  const [topVideos, setTopVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { axiosInstance } = useAuth();
  const { playVideo } = usePlayer();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [analyticsRes, trendingRes, videosRes] = await Promise.all([
        axiosInstance.get('/analytics/platform'),
        axiosInstance.get('/analytics/trending-creators?limit=10'),
        axiosInstance.get('/videos?limit=10')
      ]);
      setAnalytics(analyticsRes.data);
      setTrendingCreators(trendingRes.data);
      setTopVideos(videosRes.data || []);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getThumbnail = (video) => {
    return resolveThumbnailUrl(video, 'https://images.unsplash.com/photo-1526218626217-dc65a29bb444?w=400');
  };

  const handleVideoClick = (video, index) => {
    playVideo(video, topVideos, index);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="pb-32 px-6 max-w-7xl mx-auto">
      <div className="py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-zinc-800/60">
              <BarChart3 className="w-6 h-6 text-zinc-300" />
            </div>
            <h1 className="text-3xl font-bold text-white">
              Platform Analytics
            </h1>
          </div>
          <p className="text-zinc-400">Real-time insights into the AUDIVUE community</p>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5 text-zinc-300" />
              <span className="text-zinc-400 text-sm">Total Users</span>
            </div>
            <div className="text-3xl font-bold text-white">{formatNumber(analytics?.users?.total)}</div>
            <div className="mt-2 flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm">{analytics?.users?.premium_percentage}% Premium</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Video className="w-5 h-5 text-zinc-300" />
              <span className="text-zinc-400 text-sm">Total Videos</span>
            </div>
            <div className="text-3xl font-bold text-white">{formatNumber(analytics?.content?.total_videos)}</div>
            <div className="mt-2 text-zinc-500 text-sm">
              {formatNumber(analytics?.content?.total_views)} total views
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-green-400" />
              <span className="text-zinc-400 text-sm">Watch Hours</span>
            </div>
            <div className="text-3xl font-bold text-white">{formatNumber(analytics?.engagement?.total_watch_hours)}</div>
            <div className="mt-2 text-zinc-500 text-sm">
              {analytics?.engagement?.avg_watch_per_video}h avg/video
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-5 h-5 text-pink-400" />
              <span className="text-zinc-400 text-sm">Total Likes</span>
            </div>
            <div className="text-3xl font-bold text-white">{formatNumber(analytics?.content?.total_likes)}</div>
          </motion.div>
        </div>

        {/* Creator Economy Stats */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-gradient-to-br from-green-500/10 to-emerald-600/10 border border-green-500/30 rounded-2xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Creator Economy
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className="text-zinc-400 text-sm">Total Payouts</p>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(analytics?.creator_economy?.total_payouts)}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-zinc-300" />
              Top Genres
            </h3>
            <div className="space-y-3">
              {analytics?.top_genres?.map((genre, index) => (
                <div key={genre.genre} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-zinc-400">
                      {index + 1}
                    </span>
                    <span className="text-white">{genre.genre || 'Unknown'}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-zinc-400 text-sm">{genre.videos} videos</span>
                    <span className="text-zinc-600 mx-2">•</span>
                    <span className="text-zinc-500 text-sm">{formatNumber(genre.views)} views</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Top Videos Section */}
        {topVideos.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-400" />
                All Videos
              </h3>
              <span className="text-zinc-500 text-sm">{topVideos.length} videos</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {topVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleVideoClick(video, index)}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 mb-2">
                    <img 
                      src={getThumbnail(video)} 
                      alt={video.title} 
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                        <Play className="w-6 h-6 text-black fill-black ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 flex items-center gap-2">
                      <span className="bg-black/70 px-2 py-0.5 rounded text-xs text-white flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {formatNumber(video.views || 0)}
                      </span>
                      <span className="bg-black/70 px-2 py-0.5 rounded text-xs text-white flex items-center gap-1">
                        <Heart className="w-3 h-3" /> {formatNumber(video.likes || 0)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-white truncate">{video.title}</p>
                  <p 
                    className="text-xs text-zinc-500 hover:text-zinc-300 cursor-pointer truncate"
                    onClick={(e) => { e.stopPropagation(); navigate(`/creator/${video.user_id || video.artist_id}`); }}
                  >
                    {video.artist}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Creator Leaderboard */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Top Creators
            </h3>
            <span className="text-zinc-500 text-sm">By premium watch time</span>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-zinc-800 text-sm text-zinc-500">
              <div className="col-span-1">#</div>
              <div className="col-span-4">Creator</div>
              <div className="col-span-2">Tier</div>
              <div className="col-span-2 text-right">Watch Hours</div>
              <div className="col-span-3 text-right">Est. Earnings</div>
            </div>
            
            {analytics?.creator_leaderboard?.map((creator, index) => (
              <motion.div
                key={creator.creator_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/creator/${creator.creator_id}`)}
                className="grid grid-cols-12 gap-4 p-4 border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors cursor-pointer"
              >
                <div className="col-span-1 flex items-center">
                  {index === 0 ? (
                    <span className="text-2xl">🥇</span>
                  ) : index === 1 ? (
                    <span className="text-2xl">🥈</span>
                  ) : index === 2 ? (
                    <span className="text-2xl">🥉</span>
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400">
                      {index + 1}
                    </span>
                  )}
                </div>
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-lg">
                    {creator.tier?.badge || '🎬'}
                  </div>
                  <span className="text-white font-medium">@{creator.username}</span>
                </div>
                <div className="col-span-2 flex items-center">
                  <span 
                    className="px-2 py-1 rounded-full text-xs"
                    style={{ 
                      background: `${creator.tier?.color}20`,
                      color: creator.tier?.color
                    }}
                  >
                    {creator.tier?.name}
                  </span>
                </div>
                <div className="col-span-2 text-right flex items-center justify-end text-zinc-300">
                  {creator.watch_hours}h
                </div>
                <div className="col-span-3 text-right flex items-center justify-end">
                  <span className="text-green-400 font-medium">{formatCurrency(creator.estimated_earnings)}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trending Creators */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-400" />
              Trending This Week
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {trendingCreators.map((creator, index) => (
              <motion.div
                key={creator.creator_id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/creator/${creator.creator_id}`)}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center hover:border-zinc-600 transition-colors cursor-pointer"
              >
                <div className="relative mb-3">
                  <div 
                    className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl"
                    style={{ background: `${creator.tier?.color}20` }}
                  >
                    {creator.tier?.badge || '🎬'}
                  </div>
                  {index < 3 && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-xs">
                      🔥
                    </div>
                  )}
                </div>
                <h4 className="text-white font-medium truncate">@{creator.username}</h4>
                <p className="text-zinc-500 text-xs mt-1">{creator.tier?.name}</p>
                <div className="mt-2 text-xs">
                  <span className="text-zinc-300">{creator.weekly_watch_hours}h</span>
                  <span className="text-zinc-600 mx-1">•</span>
                  <span className="text-zinc-400">{creator.weekly_viewers} viewers</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
